import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Heart, CheckCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

export default function DonationThankYou() {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const markDonation = async () => {
      // Check if user is logged in
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          await axios.post(`${API}/api/auth/mark-donated`, {}, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          setLoading(false);
        } catch (error) {
          console.error("Error marking donation:", error);
          // Even if marking fails, show thank you page
          setLoading(false);
        }
      } else {
        // User not logged in, just show thank you
        setLoading(false);
      }
    };

    markDonation();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center px-6 py-12">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="bg-gradient-to-br from-emerald-500 to-teal-500 p-4 rounded-full animate-pulse">
                <Heart className="w-12 h-12 text-white" fill="white" />
              </div>
              <div className="absolute -top-2 -right-2">
                <CheckCircle className="w-8 h-8 text-green-600" fill="currentColor" />
              </div>
            </div>
          </div>
          <CardTitle className="text-3xl font-bold text-slate-800">
            Vielen herzlichen Dank!
          </CardTitle>
          <p className="text-slate-600 mt-2 text-lg">
            Ihre Spende wurde erfolgreich verarbeitet
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
              <p className="text-slate-600 mt-4">Verarbeite Ihre Spende...</p>
            </div>
          ) : (
            <>
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border-2 border-emerald-200">
                <div className="flex items-start space-x-4">
                  <Sparkles className="w-8 h-8 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">
                      Sie sind großartig! 🎉
                    </h3>
                    <p className="text-slate-700 mb-3">
                      Ihre Unterstützung bedeutet uns sehr viel und hilft dabei:
                    </p>
                    <ul className="space-y-2 text-slate-700">
                      <li className="flex items-start">
                        <span className="text-emerald-600 mr-2">✓</span>
                        <span>Die Server am Laufen zu halten</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-emerald-600 mr-2">✓</span>
                        <span>Neue Features zu entwickeln</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-emerald-600 mr-2">✓</span>
                        <span>Die App kontinuierlich zu verbessern</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-emerald-600 mr-2">✓</span>
                        <span>Support und Wartung bereitzustellen</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-center">
                  💙 Sie werden die Support-Seite bei zukünftigen Logins nicht mehr sehen.
                </p>
              </div>

              <Button 
                onClick={() => {
                  toast.success("Viel Spaß mit der App!");
                  navigate('/');
                }}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-lg py-6"
              >
                Zur App
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
